/**
 * Response Interceptor
 * Main orchestrator for response handling
 */

import type { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import type { ExtendedAxiosRequestConfig, InterceptorConfig } from "../types";
import { shouldLog } from "../utils/logging";
import { setupCrossTabTokenRefresh } from "./handlers/token-refresh-handler";
import { handleErrorLogging } from "./handlers/error-handler";
import { getCrossTabSync } from "../cross-tab";
import { getTokenRefreshManager } from "../utils/token-refresh-manager";
import { createApiError } from "../errors";
import { ErrorHandlerChain } from "../strategies/error-handler-chain";
import {
  TokenRefreshErrorHandler,
  RetryErrorHandler,
  FinalErrorHandler,
} from "../strategies/error-handlers";

/**
 * Creates a response handler for successful responses
 */
export function createResponseHandler(config: InterceptorConfig) {
  return (response: AxiosResponse): AxiosResponse => {
    if (shouldLog(config)) {
      console.log(
        `[API Response] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          data: response.data,
        },
      );
    }
    return response;
  };
}

/**
 * Creates a response error handler
 * Orchestrates token refresh, retry logic, and error handling
 */
export function createResponseErrorHandler(
  axiosInstance: AxiosInstance,
  config: InterceptorConfig,
) {
  const { crossTab = { enabled: true } } = config;

  const crossTabSync = getCrossTabSync();
  crossTabSync.initialize(crossTab);

  // Setup cross-tab token refresh message handling
  setupCrossTabTokenRefresh(config);

  // Create error handler chain using Chain of Responsibility pattern
  const errorHandlerChain = new ErrorHandlerChain();
  errorHandlerChain.register(new TokenRefreshErrorHandler());
  errorHandlerChain.register(new RetryErrorHandler());
  errorHandlerChain.register(new FinalErrorHandler());

  return async (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Log error and handle status-specific messages
    handleErrorLogging(error, config);

    // Create error context
    const context = {
      originalRequest,
      axiosInstance,
      config,
      retryCount: originalRequest._retryCount,
    };

    // Process error through handler chain
    const result = await errorHandlerChain.handle(error, context);

    if (result) {
      return result;
    }

    // If no handler returned a response, reject with transformed error
    const apiError = createApiError(error);
    return Promise.reject(apiError);
  };
}

/**
 * Broadcast logout event to all tabs
 */
export function broadcastLogout(): void {
  const crossTabSync = getCrossTabSync();
  crossTabSync.notifyLogout();
}

/**
 * Broadcast login event to all tabs
 */
export function broadcastLogin(payload: {
  accessToken: string;
  refreshToken?: string;
  user?: unknown;
}): void {
  const crossTabSync = getCrossTabSync();
  crossTabSync.notifyLogin(payload);
}

/**
 * Subscribe to authentication sync events across tabs
 */
export function subscribeToAuthSync(callbacks: {
  onLogin?: (payload: {
    accessToken: string;
    refreshToken?: string;
    user?: unknown;
  }) => void;
  onLogout?: () => void;
  onTokenUpdate?: (payload: {
    accessToken: string;
    refreshToken?: string;
  }) => void;
}): () => void {
  const crossTabSync = getCrossTabSync();
  return crossTabSync.subscribeToAuthEvents(callbacks);
}

/**
 * Reset token refresh state (useful for testing)
 */
export function resetRefreshState(): void {
  const tokenRefreshManager = getTokenRefreshManager();
  tokenRefreshManager.reset();
}
